import { ReactNode, useContext } from 'react';

import { Path, StatusLabel } from '@shared/components';
import { useFolderPathAsBreadcrumbs, useSearchParams } from '@shared/hooks';
import { NoPage } from '@shared/pages';
import { SignInContext } from '@modules/users';
import { FoldersNavDrawer, isTFolder, TFolder, TPath, useFolder } from '@modules/items';

import { getReviewsPath } from '../helpers';

export type TReviewBreadcrumbItem = { name: string; href?: string };

type TProps = {
  breadcrumbTail?: TReviewBreadcrumbItem[];
  children: ReactNode;
};

type TInnerProps = {
  folder: TFolder;
  productId?: string;
  breadcrumbTail: TReviewBreadcrumbItem[];
  children: ReactNode;
};

const ReviewScaffoldInner = ({ folder, productId, breadcrumbTail, children }: TInnerProps) => {
  const fullPath: TPath = [
    ...folder.resolvedPath,
    { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
    { _id: 'review', name: 'Review', accessRight: folder.accessRight, href: getReviewsPath(productId) },
    ...breadcrumbTail.map((item, index) => ({
      _id: `review-crumb-${index}`,
      name: item.name,
      accessRight: folder.accessRight,
      href: item.href,
    })),
  ];

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={folder} fullPath={fullPath} selectedValue="review" initialOpen={false} />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// `productId` selects the product's TD reviews and drives the nav drawer,
// breadcrumbs and setup context; without it the page shows QM reviews.
export const ReviewPageScaffold = ({ breadcrumbTail = [], children }: TProps) => {
  const { organization } = useContext(SignInContext);
  const { searchParams } = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const folderId = productId ?? organization.folders.qm;

  const { data, error } = useFolder(folderId, { enabled: Boolean(folderId) });

  if (!folderId) return <NoPage />;

  return (
    <StatusLabel
      data={data}
      error={error}
      loadingMessage="Loading reviews..."
      errorMessage="Loading reviews failed!"
      render={(folder) =>
        isTFolder(folder) ? (
          <ReviewScaffoldInner folder={folder as TFolder} productId={productId} breadcrumbTail={breadcrumbTail}>
            {children}
          </ReviewScaffoldInner>
        ) : (
          <NoPage />
        )
      }
    />
  );
};

export default ReviewPageScaffold;
